// SAB-587: resolve where an authenticated user lands. The pure decision (and the
// rationale for delegating NFL league choice to SAB-577) lives in landingChain.ts;
// this file is only the database IO that feeds it.
//
// SAB-587 refinement: NFL rules are MEMBERSHIP-based (nfl_league_members), not
// team-based. Prod has more NFL members than NFL teams: a member exists before a
// team is drafted, and a user who is a member of an NFL league must never be sent
// to create one. MLB rule 2 stays team-based (teams.user_id) since MLB creates a
// team at join. NFL membership and MLB teams both key on user_id but live in
// different tables, so the two sports are queried separately.

import { supabase } from '@/integrations/supabase/client';
import { pickLandingRoute, NFL_START, type LandingSignals } from '@/lib/landingChain';
import { isNflLeagueLive } from '@/lib/nflLeagueStatus';

export { NFL_HOME, MLB_HOME, NFL_CREATE, NFL_START, pickLandingRoute } from '@/lib/landingChain';
export type { LandingSignals } from '@/lib/landingChain';

async function collectLandingSignals(userId: string): Promise<LandingSignals> {
  let hasAnyNfl = false;
  let hasActiveNfl = false;
  let hasInSeasonMlb = false;

  // NFL: membership via nfl_league_members.user_id (NOT nfl_teams). NFL tables
  // are not in the generated types, matching the `as any` casts used elsewhere.
  const { data: nflMemberships, error: nflMemberErr } = await supabase
    .from('nfl_league_members' as any)
    .select('league_id')
    .eq('user_id', userId);
  if (nflMemberErr) console.warn('[landing] nfl_league_members lookup failed', nflMemberErr);
  const nflLeagueIds = [
    ...new Set(((nflMemberships as any[] | null) ?? []).map((m) => m.league_id).filter(Boolean)),
  ];
  if (nflLeagueIds.length > 0) {
    hasAnyNfl = true;
    const { data: nflLeagues, error: nflLeaguesErr } = await supabase
      .from('nfl_leagues' as any)
      .select('status')
      .in('id', nflLeagueIds);
    if (nflLeaguesErr) console.warn('[landing] nfl_leagues lookup failed', nflLeaguesErr);
    // SAB-930: was `l.status === 'active'` -- a value NOTHING has ever written to
    // nfl_leagues.status, so this signal was permanently false and landing rule 1 never
    // fired for anyone. Rules 1 and 3 both return the NFL shell, so the bug was invisible
    // except at the one intersection where rule 2 sits between them: a user with a live
    // NFL league AND an in-season MLB team was sent to MLB. The predicate now comes from
    // the shared partition, which is pinned against the SAB-928 constraint vocabulary.
    hasActiveNfl = ((nflLeagues as any[] | null) ?? []).some((l) => isNflLeagueLive(l?.status));
  }

  // MLB: ownership is user_id. Scope to in_season leagues only (rule 2).
  const { data: mlbTeams, error: mlbTeamsErr } = await supabase
    .from('teams')
    .select('league_id')
    .eq('user_id', userId);
  if (mlbTeamsErr) console.warn('[landing] teams lookup failed', mlbTeamsErr);
  const mlbLeagueIds = [
    ...new Set(((mlbTeams as { league_id: string }[] | null) ?? []).map((t) => t.league_id).filter(Boolean)),
  ];
  if (mlbLeagueIds.length > 0) {
    const { data: inSeason, error: mlbLeaguesErr } = await supabase
      .from('leagues')
      .select('id')
      .in('id', mlbLeagueIds)
      .eq('status', 'in_season');
    if (mlbLeaguesErr) console.warn('[landing] leagues lookup failed', mlbLeaguesErr);
    hasInSeasonMlb = ((inSeason as unknown[] | null) ?? []).length > 0;
  }

  return { hasActiveNfl, hasInSeasonMlb, hasAnyNfl };
}

/** Resolve the landing route for a user. Never throws; on failure lands on
 *  create/join (rule 4) rather than a wrong-sport screen. */
export async function resolveLandingRoute(userId: string): Promise<string> {
  try {
    return pickLandingRoute(await collectLandingSignals(userId));
  } catch (err) {
    console.warn('[landing] resolve failed, falling back to the cold-start chooser', err);
    return NFL_START;
  }
}
